import React, { useMemo } from 'react';
import { useTranslation } from '@/components/utils/translations';
import usePilotDeals from './usePilotDeals';
import LiveDealsTile from './LiveDealsTile';
import NewOpportunitiesTile from './NewOpportunitiesTile';
import TasksTile from './TasksTile';
import TopSignalsTile from './TopSignalsTile';

/**
 * Home for tenants with BOTH Core and Procure (pilot mode and full platform), 2026-11-04.
 * Four equal tiles, top five each; a tile's title opens its full page:
 *   Live Deals (Procurement) · New Opportunities (Market) · Tasks · Top Signals (Signals).
 * Core-only tenants keep the original Home; Procure-only tenants never reach Home (they land on
 * /Procurement), so neither is affected.
 */
export default function PilotHome({
  tenantId,
  userId,
  plan,
  people,
  accounts,
  momentumScores,
  isLoading,
  planLoading,
  signalsLoading,
  onOpenDraft,
  onAcknowledge,
}) {
  const { t } = useTranslation();
  const deals = usePilotDeals(tenantId);
  const planItems = useMemo(() => Object.values(plan || {}).flat(), [plan]);

  // Each row is as tall as its taller tile (grid stretch), so tiles side by side match; rows are not
  // forced to one height, which left the top two tiles half empty once Tasks grew.
  return (
    <div data-testid="pilot-home" className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
      <LiveDealsTile deals={deals} tenantId={tenantId} myDealsLabel={t('myDeals')} />
      <NewOpportunitiesTile tenantId={tenantId} />
      <TasksTile
        tenantId={tenantId}
        userId={userId}
        deals={deals}
        plan={planItems}
        planLoading={planLoading}
        people={people}
        accounts={accounts}
        onOpenDraft={onOpenDraft}
        onAcknowledge={onAcknowledge}
      />
      <TopSignalsTile momentumScores={momentumScores} accounts={accounts} loading={signalsLoading ?? (isLoading && !momentumScores?.length)} />
    </div>
  );
}
